/**
 * 週報匯出：**表格內容**（純函式；P1-12 頁 14）
 *
 * 為何唔喺呢度出 bytes：內容（行／欄）同「打成 Excel」係兩件事 ——
 *   · 本檔＝內容（可以喺 Node 直接測；`scripts/test-reports.mjs` 咬佢）
 *   · `report-export.ts` ＝ 用 `@coeldery/table-io` 打成 `.xlsx`／`.csv` ＋ 觸發下載
 *
 * 紅線
 *   · ⭐ **表頭文字一律由呼叫者傳入**（頁面用 `t()` 拎 i18n）—— 本檔唔准寫死任何文字
 *     （`rules` 通用章：文字唔准硬編碼；匯出檔都要跟當前語言）
 *   · ⭐ `rules §七 F1`：數字**唔准**喺呢度重算 —— 一律照 `WeeklyReport` 嘅值抄
 *   · 金額／cbm 一律**轉字串**先（`table-io` 唔知貨幣；Excel 亦唔應該當公式算）
 */
import type { WeeklyReport } from './api';

/** 匯出表頭／工作表名（**全部由頁面嘅 `t()` 傳入**） */
export interface ReportLabels {
  /** 工作表名（4 張） */
  sheetFee: string;
  sheetLedger: string;
  sheetBatches: string;
  sheetPending: string;
  /** 倉租明細欄 */
  week: string;
  openingCbm: string;
  outboundCbm: string;
  closingCbm: string;
  storageRate: string;
  storageAmount: string;
  handlingAmount: string;
  totalAmount: string;
  status: string;
  /** 出入庫摘要欄 */
  type: string;
  count: string;
  cartons: string;
  units: string;
  cbm: string;
  /** 入庫批次欄 */
  batchNo: string;
  supplier: string;
  receivedDate: string;
  receivedCbm: string;
  handlingFee: string;
  /** 未完事項：兩欄（項目／數量） */
  item: string;
  value: string;
}

/** 一格嘅值（同 `table-io` 嘅 `Cell` 一樣；唔直接 import 模組，保持本檔零依賴） */
export type Cell = string | number | null | undefined;

/** 一張工作表 */
export interface SheetRows {
  name: string;
  rows: Cell[][];
}

/** 金額／cbm → 字串（cbm 6dp、金額 2dp；同後端 `round6`／`round2` 口徑一致） */
export const n6 = (n: number): string => Number(n ?? 0).toFixed(6);
export const n2 = (n: number): string => Number(n ?? 0).toFixed(2);

/** ① 倉租明細（一行；`status` 用**機器值** `draft`／`confirmed` —— 唔翻譯，帳目要可追溯） */
export function feeSheet(report: WeeklyReport, labels: ReportLabels): SheetRows {
  const f = report.billing;
  return {
    name: labels.sheetFee,
    rows: [
      [
        labels.week,
        labels.openingCbm,
        labels.outboundCbm,
        labels.closingCbm,
        labels.storageRate,
        labels.storageAmount,
        labels.handlingAmount,
        labels.totalAmount,
        labels.status,
      ],
      [
        `${report.weekStart} ~ ${report.weekEnd}`,
        n6(f.openingCbm),
        n6(f.outboundCbm),
        n6(f.closingCbm),
        n2(f.storageRate),
        n2(f.storageAmount),
        n2(f.handlingAmount),
        n2(f.totalAmount),
        f.status,
      ],
    ],
  };
}

/** ② 出入庫摘要（帳本按移動類別；來源 ＝ `stock_movements`） */
export function ledgerSheet(report: WeeklyReport, labels: ReportLabels): SheetRows {
  return {
    name: labels.sheetLedger,
    rows: [
      [labels.type, labels.count, labels.cartons, labels.units, labels.cbm],
      ...report.ledger.byType.map((r) => [r.type, r.count, r.cartons, r.units, n6(r.cbm)]),
    ],
  };
}

/** ③ 入庫批次（該週收貨；手續費歸屬週） */
export function batchSheet(report: WeeklyReport, labels: ReportLabels): SheetRows {
  return {
    name: labels.sheetBatches,
    rows: [
      [labels.batchNo, labels.supplier, labels.receivedDate, labels.receivedCbm, labels.handlingFee, labels.status],
      ...report.intakeBatches.map((b) => [
        b.batchNo,
        `${b.supplierCode} ${b.supplierName}`,
        b.receivedDate ?? '',
        n6(b.receivedCbm),
        n2(b.handlingFee),
        b.status,
      ]),
    ],
  };
}

/**
 * ④ 未完事項（項目／數量）。
 * ⚠️ 項目文字由呼叫者傳入（`pendingRows`），因為佢係 UI 文案（i18n）唔係報表固定欄名。
 */
export function pendingSheet(
  labels: ReportLabels,
  pendingRows: ReadonlyArray<readonly [string, string | number]>,
): SheetRows {
  return {
    name: labels.sheetPending,
    rows: [[labels.item, labels.value], ...pendingRows.map(([k, v]) => [k, String(v)])],
  };
}

/** 四張工作表（順序固定：倉租 → 帳本 → 批次 → 未完事項） */
export function reportSheets(
  report: WeeklyReport,
  labels: ReportLabels,
  pendingRows: ReadonlyArray<readonly [string, string | number]> = [],
): SheetRows[] {
  return [
    feeSheet(report, labels),
    ledgerSheet(report, labels),
    batchSheet(report, labels),
    pendingSheet(labels, pendingRows),
  ];
}

/**
 * CSV 用：多張表串成一大張（每張前面加一行**工作表名** ＋ 中間空一行）。
 * 為何唔出 4 個 CSV：CSV 冇工作表概念，一個檔畀人睇得清楚最實際。
 */
export function reportCsvRows(
  report: WeeklyReport,
  labels: ReportLabels,
  pendingRows: ReadonlyArray<readonly [string, string | number]> = [],
): Cell[][] {
  const out: Cell[][] = [];
  for (const sheet of reportSheets(report, labels, pendingRows)) {
    out.push([sheet.name], ...sheet.rows, []);
  }
  return out;
}
